import "./btn-workspace-dropdown.css"
import React, { HTMLAttributes, useEffect, useRef, useState, type JSX } from "react"
import * as Icons from "@nova/component-icons"
import { createPortal } from "react-dom"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import classNames from "classnames"
import { formatPhoneNumber, validateEmailAddress } from "@nova/util-strings"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { EmailList, zIndex } from "@nova/constants"
import { ContactList } from "@nova/schemas"

type UserDropdownButtons = {
    userId?: string
    fullName?: string
    email?: string
    CollaboratorComponent: JSX.Element
    emailList?: EmailList
}

export type UserContext = {
    userId?: string
    email?: string
    fullName?: string
    contactList?: ContactList
}

type ButtonSplitProps = HTMLAttributes<HTMLElement> & {
    userDropdownButtons: UserDropdownButtons[]
    widthClass: string
    setSelectedUser: (user: UserContext) => void
    handleChange?: (value: string) => void
    onClear?: () => void
    maxDropdownHeight?: number
    //use portalId to render the dropdown container in a different area of the DOM, useful for getting it outside of scrollable overflow containers
    portalPositioning?: {
        portalId: string
        relativeOffset: number
    }
    displaySelection?: boolean
}

/**
 *
 * Similar to btn-workspace-dropdown but the header button is an input that you can type into to filter what dropdown items appear
 */
export const ButtonWorkspaceUserDropdown = ({
    className,
    userDropdownButtons,
    setSelectedUser,
    widthClass,
    style,
    maxDropdownHeight,
    portalPositioning,
    displaySelection,
    onClear,
    handleChange,
    ...rest
}: ButtonSplitProps) => {
    const containerRef = useRef<HTMLDivElement>(null)

    const [filterInput, setFilterInput] = useState("")
    const [user, setUser] = useState<UserContext | undefined>()
    const [menuOpen, setMenuOpen] = useState(false)

    const container = containerRef.current
    const boundingClientRect = container?.getBoundingClientRect()
    const portal = document.getElementById(portalPositioning?.portalId || "")

    const [isFocused, setIsFocused] = useState(false)

    const emptyInput = filterInput === "" || !filterInput
    return (
        <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
            <div
                ref={containerRef}
                className={classNames(
                    "h-[2.5rem] button-workspace-dropdown--container relative bg-gray-700 cursor-pointer rounded-lg text-gray-0",
                    widthClass,
                    className
                )}
                style={style}
                {...rest}
            >
                <div className="relative w-full">
                    <div className="absolute left-2 top-[50%] -translate-y-[50%]">
                        <Icons.Icon Icon={Icons.Plus} className="text email-list-box__plus" />
                    </div>
                    <input
                        type="text"
                        placeholder={"Enter a name or an email"}
                        className={classNames("email-list-box__input !text-sm !pl-10", {
                            "email-list-box__input--active": !emptyInput,
                        })}
                        value={displaySelection && user ? user.fullName || user.email : filterInput}
                        onClick={() => setMenuOpen(true)}
                        onChange={(event) => {
                            if (!menuOpen) setMenuOpen(true)
                            setFilterInput(event.target.value)
                            setUser(undefined)
                            handleChange?.(event.target.value)
                        }}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                    {isFocused && (
                        <Icons.ExtraSmallIcon
                            Icon={Icons.XClose}
                            onMouseDown={(e) => e.preventDefault()} //prevents the input from blurring when clicking the icon
                            onClick={() => {
                                setFilterInput("")
                                setUser(undefined)
                                onClear?.()
                            }}
                            className="absolute right-4 top-3 text-gray-300 cursor-pointer"
                        />
                    )}
                </div>
                {portal &&
                    boundingClientRect &&
                    createPortal(
                        <DropdownComponent
                            filterInput={filterInput}
                            setFilterInput={setFilterInput}
                            userDropdownButtons={userDropdownButtons}
                            maxDropdownHeight={maxDropdownHeight}
                            setMenuOpen={setMenuOpen}
                            widthClass={widthClass}
                            menuOpen={menuOpen}
                            setSelectedUser={setSelectedUser}
                            setUser={setUser}
                            style={{
                                top:
                                    (boundingClientRect.top || 0) +
                                    (portalPositioning?.relativeOffset || 0),
                                left: boundingClientRect.left,
                                position: "fixed",
                                zIndex: zIndex.HighPriorityModalDropdown,
                            }}
                        />,
                        portal as HTMLElement
                    )}
                {(!portalPositioning || !boundingClientRect) && (
                    <DropdownComponent
                        filterInput={filterInput}
                        setFilterInput={setFilterInput}
                        userDropdownButtons={userDropdownButtons}
                        setSelectedUser={setSelectedUser}
                        maxDropdownHeight={maxDropdownHeight}
                        widthClass={widthClass}
                        menuOpen={menuOpen}
                        setMenuOpen={setMenuOpen}
                        setUser={setUser}
                        style={{ zIndex: zIndex.HighPriorityModalDropdown }}
                    />
                )}
            </div>
        </ClickAwayListener>
    )
}

export const DropdownComponent = ({
    filterInput,
    setFilterInput,
    setSelectedUser,
    setUser,
    maxDropdownHeight,
    userDropdownButtons,
    widthClass,
    style,
    menuOpen,
    setMenuOpen,
    dropdownClassName,
    allowNonMemberEmail = true,
}: Omit<ButtonSplitProps, "label" | "onClick"> & {
    filterInput: string
    setFilterInput: (value: string) => void
    setUser: (user: UserContext) => void
    menuOpen: boolean
    setMenuOpen: (value: boolean) => void
    dropdownClassName?: string
    allowNonMemberEmail?: boolean
}) => {
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
    const dropdownRef = useRef<HTMLDivElement | null>(null)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

    const filteredButtons = userDropdownButtons.filter((user) => {
        const searchTerm = `${user.fullName || user.userId} ${user.email}`
        const formattedPhoneNumberInput = formatPhoneNumber(filterInput.toLocaleLowerCase())
        return (
            searchTerm.toLowerCase().includes(filterInput.toLowerCase()) ||
            (formattedPhoneNumberInput &&
                searchTerm.toLowerCase().includes(formattedPhoneNumberInput))
        )
    })

    const isInputValidEmail = validateEmailAddress(filterInput)
    if (
        isInputValidEmail &&
        !filteredButtons.find((user) => user.userId || user.email === filterInput) &&
        allowNonMemberEmail
    ) {
        filteredButtons.push({
            userId: undefined,
            fullName: filterInput,
            email: filterInput,
            CollaboratorComponent: (
                <div className="text-white-900 flex gap-4 items-center">
                    <div className="h-8 w-8 text-white-400 border-white-400 border flex items-center justify-center rounded-xl">
                        <Icons.Icon Icon={Icons.NoAccount} className="text-white-400" />
                    </div>
                    {filterInput}
                </div>
            ),
        })
    }

    const showDropdown = filterInput.length > 0 && menuOpen && filteredButtons.length > 0

    useEffect(() => {
        if (!menuOpen) {
            setHighlightedIndex(null)
        }
    }, [menuOpen])

    useEffect(() => {
        if (menuOpen && filteredButtons.length && highlightedIndex === null) {
            setHighlightedIndex(0)
        }
    }, [filteredButtons.length, highlightedIndex, menuOpen])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!showDropdown) return

            if (e.key === "ArrowDown") {
                e.preventDefault()
                setHighlightedIndex((prev) =>
                    prev === null || prev === filteredButtons.length - 1 ? 0 : prev + 1
                )
            } else if (e.key === "ArrowUp") {
                e.preventDefault()
                setHighlightedIndex((prev) =>
                    prev === null || prev === 0 ? filteredButtons.length - 1 : prev - 1
                )
            } else if (e.key === "Enter") {
                e.preventDefault()
                if (highlightedIndex !== null) {
                    setSelectedUser(filteredButtons[highlightedIndex])
                    setMenuOpen(false)
                    setFilterInput("")
                }
            } else if (e.key === "Escape") {
                setMenuOpen(false)
            }
        }

        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [
        showDropdown,
        highlightedIndex,
        filteredButtons,
        setSelectedUser,
        setMenuOpen,
        setFilterInput,
    ])

    // Scroll into view when highlightedIndex changes
    useEffect(() => {
        if (highlightedIndex !== null && itemRefs.current[highlightedIndex]) {
            itemRefs.current[highlightedIndex]?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            })
        }
    }, [highlightedIndex])

    return (
        <ScrollableArea
            ref={dropdownRef}
            className={classNames(
                "absolute top-10 left-0 z-50 max-h-[200px] w-full flex flex-col items-start rounded-md bg-gray-700 py-1 mt-0.5",
                {
                    invisible: !showDropdown,
                },
                widthClass,
                dropdownClassName
            )}
            style={{
                maxHeight: maxDropdownHeight,
                ...style,
            }}
        >
            {filteredButtons.map((user, idx) => (
                <button
                    key={user.userId || idx}
                    ref={(el) => {
                        itemRefs.current[idx] = el
                    }}
                    className={classNames(
                        "button-workspace-dropdown--dropdown-button text-gray-0 w-full flex items-center gap-3 text-left pl-4 pr-3 py-2 my-0.5 hover:bg-gray-500",
                        {
                            "bg-gray-500": highlightedIndex === idx,
                        }
                    )}
                    onClick={() => {
                        setSelectedUser(user)
                        setUser(user)
                        setMenuOpen(false)
                        setFilterInput("")
                    }}
                >
                    {user.CollaboratorComponent}
                </button>
            ))}
        </ScrollableArea>
    )
}
